import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, MessageCircle, ArrowRight, Search, X, ArrowUpRight } from 'lucide-react';
import BookingModal from '../components/BookingModal';
import WallOfLove from '../components/WallOfLove';
import { serviceCategories, searchServices, getAllServices } from '../data/servicesData';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const Home = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const stickySectionRef = useRef(null);
  const [imageFixed, setImageFixed] = useState(false);
  const videoRef = useRef(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(() => Math.floor(Math.random() * 3));
  const navigate = useNavigate();
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);

  // Video playlist - rotates through all 3 videos
  const videos = [
    '/salon-video-1.mp4',
    '/salon-video.mp4', 
    '/salon-video-3.mp4'
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (stickySectionRef.current) {
        const rect = stickySectionRef.current.getBoundingClientRect();
        // Image becomes fixed when section enters viewport
        // and stops being fixed when section ends
        setImageFixed(rect.top <= 0 && rect.bottom > window.innerHeight);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle video ended - play next video in sequence
  const handleVideoEnded = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
  };

  // Update video source when index changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [currentVideoIndex]);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim()) {
      const results = searchServices(searchQuery);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allServices = getAllServices();

  const stats = [
    { number: 5000, suffix: '+', label: 'Happy Clients' },
    { number: 12, suffix: '+', label: 'Expert Artists' },
    { number: 4, suffix: '', label: 'Locations' },
    { number: 50, suffix: '+', label: 'Services' },
  ];

  return (
    <>
      <Helmet>
        <title>BLR Nail Club - Best Nail Salon in Bangalore | RR Nagar, Basaveshwarnagar, Varthur, Sahakarnagar, Sarjapur</title>
        <meta name="description" content="BLR Nail Club - Bangalore's #1 nail salon. Best nail extensions, gel nails, nail art, manicure & pedicure in RR Nagar, Basaveshwarnagar, Sahakarnagar, Varthur, Sarjapur Road & Gunjur. Book now!" />
        <meta name="keywords" content="nail salon Bangalore, best nail salon RR Nagar, nail extensions Bangalore, gel nails near me, nail art Basaveshwarnagar, nail salon Sahakarnagar, nail salon Varthur, nail salon Sarjapur Road, nail salon Gunjur, manicure pedicure Bangalore, lash extensions Bangalore, hair salon RR Nagar, bridal makeup Bangalore, beauty salon Varthur, nail spa near me, best nails Bangalore, nail art near me, gel nail extensions, acrylic nails Bangalore" />
        <link rel="canonical" href="https://www.blrnailclub.in/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.blrnailclub.in/" />
        <meta property="og:title" content="BLR Nail Club - Best Nail & Beauty Salon in Bangalore" />
        <meta property="og:description" content="Premium nail extensions, gel nails, lash extensions, hair & skin services. 3 convenient locations in Bangalore. Book your appointment today!" />
        <meta property="og:image" content="https://www.blrnailclub.in/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BLR Nail Club - Best Nail Salon in Bangalore" />
        <meta name="twitter:description" content="Premium nail & beauty services. Nail extensions, gel nails, lash extensions & more. Book now!" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="BLR Nail Club" />
        <meta name="geo.region" content="IN-KA" />
        <meta name="geo.placename" content="Bangalore" />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BeautySalon",
            "name": "BLR Nail Club",
            "description": "Premium nail and beauty studio offering nail extensions, gel nails, nail art, lash extensions, hair services, skin treatments and bridal makeup in Bangalore.",
            "url": "https://www.blrnailclub.in",
            "logo": "https://www.blrnailclub.in/logo.png",
            "image": "https://www.blrnailclub.in/og-image.jpg",
            "telephone": "+91-7204602211",
            "email": "blrnailclub@gmail.com",
            "priceRange": "₹₹",
            "address": [
              {
                "@type": "PostalAddress",
                "streetAddress": "N2VK Arcade, 1154, Jawaharlal Nehru Rd",
                "addressLocality": "Rajarajeshwari Nagar",
                "addressRegion": "Bangalore",
                "postalCode": "560098",
                "addressCountry": "IN"
              },
              {
                "@type": "PostalAddress",
                "streetAddress": "LGF, No. 9, West of Chord Road",
                "addressLocality": "Basaveshwarnagar",
                "addressRegion": "Bangalore",
                "postalCode": "560079",
                "addressCountry": "IN"
              },
              {
                "@type": "PostalAddress",
                "streetAddress": "CSK Mall, Gunjur",
                "addressLocality": "Varthur",
                "addressRegion": "Bangalore",
                "postalCode": "560087",
                "addressCountry": "IN"
              }
            ],
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "12.9267",
              "longitude": "77.5192"
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                "opens": "10:00",
                "closes": "20:00"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "529"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Beauty Services",
              "itemListElement": [
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Nail Extensions"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Gel Nails"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Nail Art"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Lash Extensions"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Hair Styling"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Facials"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Bridal Makeup"}}
              ]
            },
            "sameAs": [
              "https://www.instagram.com/blr_nailclub",
              "https://www.facebook.com/blrnailclub"
            ],
            "potentialAction": {
              "@type": "ReserveAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://www.blrnailclub.in/contact",
                "actionPlatform": [
                  "http://schema.org/DesktopWebPlatform",
                  "http://schema.org/MobileWebPlatform"
                ]
              },
              "result": {
                "@type": "Reservation",
                "name": "Book Appointment"
              }
            }
          })}
        </script>
      </Helmet>

      <main className="bg-black" data-testid="home-page">
      {/* Hero Section - Full Screen Video */}
      <section className="h-screen relative flex items-center justify-center overflow-hidden">
        {/* Full Screen Video Background - Rotates through 3 videos */}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnded}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={videos[currentVideoIndex]} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Hero Content */}
        <div className="relative z-10 text-center px-6">
          <h1 className="text-white text-5xl md:text-7xl lg:text-8xl font-light tracking-[0.15em] mb-6">
            BLR NAIL CLUB
          </h1>
          <p className="text-white/70 text-sm md:text-base tracking-[0.3em] uppercase font-light mb-12">
            Premium Nail & Beauty Studio
          </p>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="inline-block border border-[#B8860B] text-[#B8860B] px-10 py-4 text-sm tracking-[0.2em] uppercase font-light hover:bg-[#B8860B] hover:text-black transition-all duration-300"
            data-testid="hero-book-button"
          >
            Book Your Experience
          </button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10">
          <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <div className="w-px h-16 bg-white/20 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1/3 bg-[#B8860B] animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Sticky Background Section - Extends through About, Services, and Stats */}
      <section ref={stickySectionRef} className="relative" style={{ minHeight: '300vh' }}>
        {/* Fixed Background Image - Dark moody salon interior with velvet chairs */}
        <div 
          className={`w-full h-screen left-0 ${imageFixed ? 'fixed top-0' : 'absolute'}`}
          style={{ zIndex: 1 }}
        >
          <img
            src="https://images.unsplash.com/photo-1688973120190-ab1dcdab8d9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzYWxvbiUyMGRhcmslMjBpbnRlcmlvciUyMGdyZWVuJTIwdmVsdmV0JTIwY2hhaXIlMjBnb2xkJTIwbWlycm9yJTIwbW9vZHl8ZW58MHx8fHwxNzc0NDc5Mzc1fDA&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Luxury Salon Interior with Velvet Chairs"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* About Content - First Block */}
        <div className="relative z-10 min-h-screen flex items-center">
          <div className="w-full max-w-4xl mx-auto px-8 md:px-16 py-32">
            <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-8 font-light">
              Welcome to BLR Nail Club
            </h2>
            
            <h3 className="text-white text-4xl md:text-5xl lg:text-6xl font-light tracking-wide mb-10 leading-tight drop-shadow-lg">
              Where Beauty<br />Meets Perfection
            </h3>

            <p className="text-white text-lg md:text-xl leading-[1.9] mb-8 font-light drop-shadow-md max-w-2xl">
              BLR Nail Club is Bengaluru's premier destination for luxury nail and beauty services. 
              We bring together expert artisans, premium products, and a serene atmosphere to create 
              an unforgettable experience.
            </p>

            <p className="text-white/90 text-base md:text-lg leading-[1.9] mb-12 font-light drop-shadow-md max-w-2xl">
              From stunning nail extensions and intricate nail art to professional lash extensions 
              and PMU services, we offer a complete range of beauty treatments.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                className="inline-block bg-[#B8860B] text-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#9A7B0A] transition-all duration-300 text-center"
              >
                Our Story
              </Link>
              <Link
                to="/services"
                className="inline-block border border-white text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-light hover:bg-white hover:text-black transition-all duration-300 text-center"
              >
                View Services
              </Link>
            </div>
          </div>
        </div>

        {/* Services Section - Second Block - Search Bar */}
        <div className="relative z-10 min-h-screen py-24 flex items-center">
          <div className="w-full max-w-4xl mx-auto px-8 md:px-16">
            <div className="text-center mb-12">
              <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4 font-light">
                What We Offer
              </h2>
              <h3 className="text-white text-3xl md:text-5xl font-light tracking-wide drop-shadow-lg mb-6">
                Find Your Perfect Service
              </h3>
              <p className="text-white/60 font-light max-w-xl mx-auto">
                Search from our wide range of premium nail, lash, makeup, and skin services
              </p>
            </div>

            {/* Search Bar */}
            <div ref={searchRef} className="relative">
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40" size={22} />
                <input
                  type="text"
                  placeholder="Search services (e.g., nail extensions, lash extensions...)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsSearchOpen(true);
                  }}
                  onFocus={() => setIsSearchOpen(true)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg py-5 pl-14 pr-14 text-white text-lg placeholder:text-white/40 focus:outline-none focus:border-[#B8860B]/50 transition-colors"
                  data-testid="home-service-search"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              {/* Search Dropdown */}
              {isSearchOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#0d0d0d] border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50 max-h-[60vh] overflow-y-auto">
                  {/* Category Tabs in Dropdown */}
                  <div className="sticky top-0 bg-[#0d0d0d] border-b border-white/10 p-3 flex gap-2 overflow-x-auto">
                    <span className="text-white/30 text-xs uppercase tracking-wider py-1 px-2 whitespace-nowrap">Browse:</span>
                    {serviceCategories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/services/${cat.slug}`}
                        onClick={() => setIsSearchOpen(false)}
                        className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/70 hover:bg-[#B8860B]/20 hover:text-[#B8860B] transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>

                  {searchQuery ? (
                    searchResults.length > 0 ? (
                      <>
                        <div className="px-4 py-3 border-b border-white/10 bg-[#111]">
                          <span className="text-white/50 text-xs tracking-wider uppercase">
                            Search Results ({searchResults.length})
                          </span>
                        </div>
                        {searchResults.map((service) => (
                          <Link
                            key={service.id}
                            to={`/services/${service.parentSlug}/${service.slug}`}
                            onClick={() => {
                              setIsSearchOpen(false);
                              setSearchQuery('');
                            }}
                            className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors border-b border-white/5"
                          >
                            <img
                              src={service.image}
                              alt={service.name}
                              className="w-14 h-14 object-cover rounded"
                            />
                            <div className="flex-1 text-left">
                              <div className="flex items-center gap-2">
                                <h4 className="text-white font-light">{service.name}</h4>
                                {service.trending && (
                                  <span className="text-[10px] bg-[#B8860B]/20 text-[#B8860B] px-2 py-0.5 rounded">
                                    Trending
                                  </span>
                                )}
                              </div>
                              <p className="text-white/50 text-sm mt-1">
                                {service.parentCategory} • {service.services.length} treatments
                              </p>
                            </div>
                            <ArrowUpRight className="text-[#B8860B]/50 flex-shrink-0" size={16} />
                          </Link>
                        ))}
                      </>
                    ) : (
                      <div className="p-6 text-center">
                        <p className="text-white/50">No services found for "{searchQuery}"</p>
                        <p className="text-white/30 text-sm mt-2">Try "nails", "lashes", or "makeup"</p>
                      </div>
                    )
                  ) : (
                    <>
                      {serviceCategories.map((category) => (
                        <div key={category.id}>
                          <div className="px-4 py-3 bg-[#111] border-b border-white/10 flex items-center justify-between">
                            <span className="text-[#B8860B] text-xs tracking-wider uppercase font-medium">
                              {category.name}
                            </span>
                            <Link 
                              to={`/services/${category.slug}`}
                              onClick={() => setIsSearchOpen(false)}
                              className="text-white/30 text-xs hover:text-[#B8860B] transition-colors"
                            >
                              View All →
                            </Link>
                          </div>
                          {category.subCategories.slice(0, 3).map((subCat) => (
                            <Link
                              key={subCat.id}
                              to={`/services/${category.slug}/${subCat.slug}`}
                              onClick={() => {
                                setIsSearchOpen(false);
                                setSearchQuery('');
                              }}
                              className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors border-b border-white/5"
                            >
                              <img
                                src={subCat.image}
                                alt={subCat.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                              <div className="flex-1 text-left min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-white font-light text-sm truncate">{subCat.name}</h4>
                                  {subCat.trending && (
                                    <span className="text-[9px] bg-[#B8860B]/20 text-[#B8860B] px-1.5 py-0.5 rounded flex-shrink-0">
                                      Trending
                                    </span>
                                  )}
                                </div>
                                <p className="text-white/50 text-xs mt-1">
                                  {subCat.services.length} treatments
                                </p>
                              </div>
                              <ArrowUpRight className="text-[#B8860B]/50 flex-shrink-0" size={14} />
                            </Link>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {serviceCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/services/${cat.slug}`}
                  className="px-5 py-2 bg-white/5 border border-white/10 text-white/70 text-sm tracking-wider hover:border-[#B8860B]/50 hover:text-[#B8860B] transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Section - Third Block */}
        <div className="relative z-10 min-h-screen flex items-center">
          <div className="w-full max-w-7xl mx-auto px-8 md:px-16 py-20">
            <div className="bg-black/40 backdrop-blur-sm p-12 md:p-16 rounded-lg border border-white/10">
              <h3 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-12 font-light text-center">
                Our Achievements
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-[#B8860B] text-4xl md:text-5xl lg:text-6xl font-light mb-3">
                      <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                    </div>
                    <p className="text-white/80 text-xs md:text-sm tracking-[0.2em] uppercase font-light">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Book Your Appointment Section - WHITE BACKGROUND */}
      <section className="bg-white py-24 relative z-20">
        <div className="max-w-4xl mx-auto px-8 md:px-16 text-center">
          <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-6 font-light">
            Ready to Experience Luxury?
          </h2>
          <h3 className="text-black text-3xl md:text-4xl lg:text-5xl font-light tracking-wide mb-8">
            Book Your Appointment
          </h3>
          <p className="text-black/60 text-base font-light mb-12 max-w-xl mx-auto">
            Visit any of our locations across Bengaluru or book online for a premium beauty experience.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <a
              href="https://wa.me/917204602211?text=Hi!%20I'd%20like%20to%20book%20an%20appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 bg-[#25D366] text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#20BD5A] transition-colors"
            >
              <MessageCircle size={20} strokeWidth={1.5} />
              WhatsApp
            </a>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="flex items-center justify-center gap-3 bg-[#B8860B] text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
            >
              Book Online
            </button>
            <a
              href="tel:+917204602211"
              className="flex items-center justify-center gap-3 border border-black/30 text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:border-[#B8860B] hover:text-[#B8860B] transition-colors"
            >
              <Phone size={20} strokeWidth={1.5} />
              Call Now
            </a>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-12 border-t border-black/10">
            <div>
              <p className="text-[#B8860B] text-[10px] tracking-[0.2em] uppercase mb-2">Phone</p>
              <a href="tel:+917204602211" className="text-black text-lg font-light hover:text-[#B8860B] transition-colors">
                +91 72046 02211
              </a>
            </div>
            <div>
              <p className="text-[#B8860B] text-[10px] tracking-[0.2em] uppercase mb-2">Locations</p>
              <Link to="/about#locations" className="text-black text-lg font-light hover:text-[#B8860B] transition-colors">
                RR Nagar, Basaveshwarnagar, Sahakarnagar & Gunjur
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Marquee Section - White Background - All 3 logos rotating */}
      <section className="bg-white py-12 relative z-20 overflow-hidden">
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-24 md:gap-32 mx-16 md:mx-24">
                <img 
                  src="/logo.png" 
                  alt="BLR Nail Club" 
                  className="h-24 md:h-32 w-auto flex-shrink-0"
                />
                <img 
                  src="/benefits-logo.png" 
                  alt="Benefits Salon" 
                  className="h-24 md:h-32 w-auto flex-shrink-0"
                />
                <img 
                  src="/fyne-logo.png" 
                  alt="FYNE" 
                  className="h-24 md:h-32 w-auto flex-shrink-0"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hide scrollbar + Marquee Animation */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 15s linear infinite;
        }
      `}</style>

      {/* Wall of Love - Testimonials Section */}
      <WallOfLove />

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </main>
    </>
  );
};

export default Home;
